import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import type { Json } from '@/integrations/supabase/types';

export interface DealForMerge {
  id: string;
  name: string;
  total_amount: number;
  source: string | null;
  created_at: string;
  stage: string;
  payments_count: number;
}

export interface ContactWithDuplicateDeals {
  contact_id: string;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  total_deals: number;
  deals_qb: number;
  deals: DealForMerge[];
}

interface MergeResult {
  success: boolean;
  payments_moved: number;
  transactions_moved: number;
  new_total: number;
  deleted_deal: string;
}

export function useMergeDeals() {
  const [duplicates, setDuplicates] = useState<ContactWithDuplicateDeals[]>([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchDuplicates = useCallback(async (onlyQuickBooks = false) => {
    setLoading(true);
    setError(null);

    try {
      // First, get contacts with multiple deals
      const { data: contactsData, error: contactsError } = await supabase
        .from('contacts')
        .select(`
          id,
          first_name,
          last_name,
          primary_email
        `)
        .order('first_name');

      if (contactsError) throw contactsError;

      // Get all deals with payment counts
      const { data: dealsData, error: dealsError } = await supabase
        .from('deals')
        .select(`
          id,
          name,
          total_amount,
          source,
          created_at,
          stage,
          contact_id
        `);

      if (dealsError) throw dealsError;

      // Get payment counts per deal
      const { data: paymentCounts, error: paymentsError } = await supabase
        .from('payments')
        .select('deal_id');

      if (paymentsError) throw paymentsError;

      // Count payments per deal
      const paymentCountMap: Record<string, number> = {};
      paymentCounts?.forEach(p => {
        paymentCountMap[p.deal_id] = (paymentCountMap[p.deal_id] || 0) + 1;
      });

      // Group deals by contact
      const dealsByContact: Record<string, DealForMerge[]> = {};
      dealsData?.forEach(deal => {
        if (!deal.contact_id) return;
        if (!dealsByContact[deal.contact_id]) {
          dealsByContact[deal.contact_id] = [];
        }
        dealsByContact[deal.contact_id].push({
          id: deal.id,
          name: deal.name || 'Sem nome',
          total_amount: deal.total_amount || 0,
          source: deal.source,
          created_at: deal.created_at,
          stage: deal.stage || '',
          payments_count: paymentCountMap[deal.id] || 0
        });
      });

      // Filter contacts with multiple deals
      const duplicateContacts: ContactWithDuplicateDeals[] = [];
      contactsData?.forEach(contact => {
        const deals = dealsByContact[contact.id] || [];
        if (deals.length > 1) {
          const dealsQb = deals.filter(d => d.source === 'quickbooks').length;
          
          // If onlyQuickBooks filter is active, skip contacts without QB deals
          if (onlyQuickBooks && dealsQb === 0) return;

          duplicateContacts.push({
            contact_id: contact.id,
            first_name: contact.first_name,
            last_name: contact.last_name,
            primary_email: contact.primary_email,
            total_deals: deals.length,
            deals_qb: dealsQb,
            deals: deals.sort((a, b) => 
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )
          });
        }
      });

      // Sort by total deals descending
      duplicateContacts.sort((a, b) => b.total_deals - a.total_deals);

      setDuplicates(duplicateContacts);
    } catch (err) {
      console.error('Error fetching duplicates:', err);
      setError(err instanceof Error ? err : new Error('Erro ao buscar duplicados'));
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os deals duplicados',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const mergeDeal = useCallback(async (
    dealDestinoId: string,
    dealOrigemId: string,
    userId?: string
  ): Promise<MergeResult | null> => {
    setMerging(true);

    try {
      const { data, error } = await supabase.rpc('merge_deals', {
        p_deal_destino_id: dealDestinoId,
        p_deal_origem_id: dealOrigemId,
        p_user_id: userId || null
      });

      if (error) throw error;

      const result = data as unknown as MergeResult;

      toast({
        title: 'Merge realizado',
        description: `${result.payments_moved} payments movidos. Deal "${result.deleted_deal}" deletado.`
      });

      return result;
    } catch (err) {
      console.error('Error merging deals:', err);
      toast({
        title: 'Erro no merge',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      return null;
    } finally {
      setMerging(false);
    }
  }, [toast]);

  const mergeMultipleDeals = useCallback(async (
    dealDestinoId: string,
    dealOrigemIds: string[],
    userId?: string
  ): Promise<boolean> => {
    setMerging(true);
    let successCount = 0;

    try {
      for (const origemId of dealOrigemIds) {
        const { data, error: rpcError } = await supabase.rpc('merge_deals', {
          p_deal_destino_id: dealDestinoId,
          p_deal_origem_id: origemId,
          p_user_id: userId || null
        });

        if (rpcError) {
          console.error('Error merging deal:', origemId, rpcError);
        } else {
          successCount++;
        }
      }

      if (successCount === dealOrigemIds.length) {
        toast({
          title: 'Merge completo',
          description: `${successCount} deals mesclados com sucesso`
        });
        return true;
      } else if (successCount > 0) {
        toast({
          title: 'Merge parcial',
          description: `${successCount}/${dealOrigemIds.length} deals mesclados`,
          variant: 'destructive'
        });
        return true;
      } else {
        toast({
          title: 'Erro no merge',
          description: 'Nenhum deal foi mesclado',
          variant: 'destructive'
        });
        return false;
      }
    } catch (err) {
      console.error('Error in merge:', err);
      toast({
        title: 'Erro no merge',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      return false;
    } finally {
      setMerging(false);
    }
  }, [toast]);

  return {
    duplicates,
    loading,
    merging,
    error,
    fetchDuplicates,
    mergeDeal,
    mergeMultipleDeals
  };
}
